import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ShareLink } from '@katsquare/types';
import { Button, Card, Spinner } from '../../components/ui';
import { IconClose } from '../../components/icons';
import { useToast } from '../../components/Toast';
import {
  ApiRequestError,
  createShareLink,
  listShareLinks,
  revokeShareLink,
  updateShareLink,
} from '../../lib/api';

/**
 * Mirrors SHARE_EXPIRY_DAY_CHOICES in @katsquare/types. Value-importing it would
 * type-check but break `vite build` — the types barrel is CJS, so rollup can't
 * trace named value exports out of it. Types-only imports are fine.
 */
const EXPIRY_DAY_CHOICES = [7, 14, 30, 90];

/**
 * Create and manage external review links for one PDF.
 *
 * The consent step is not decoration. A share link is a capability URL with no
 * password and no identity check — anyone who ends up holding it can read the
 * proof and comment anonymously. The person creating it has to see that stated
 * plainly and tick the box before a link exists.
 */
export function ShareDialog({ proofId, onClose }: { proofId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const links = useQuery({ queryKey: ['share-links', proofId], queryFn: () => listShareLinks(proofId) });

  const [acknowledged, setAcknowledged] = useState(false);
  const [canComment, setCanComment] = useState(true);
  const [allowDownload, setAllowDownload] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(30);
  const [planBlocked, setPlanBlocked] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['share-links', proofId] });

  const create = useMutation({
    mutationFn: () => createShareLink(proofId, { canComment, allowDownload, expiresInDays }),
    onSuccess: async (link) => {
      await invalidate();
      void copy(link.url, toast);
      setAcknowledged(false);
    },
    onError: (e: Error) => {
      if (e instanceof ApiRequestError && e.body?.code === 'plan.share_links_business_only') {
        setPlanBlocked(true);
        return;
      }
      toast(e.message);
    },
  });

  const live = (links.data ?? []).filter((l) => !l.revokedAt && !isExpired(l));
  const past = (links.data ?? []).filter((l) => l.revokedAt || isExpired(l));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <Card className="w-full max-w-lg p-0">
        <div className="flex items-center gap-2 border-b border-neutral-200 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-neutral-900">Share with people outside</h2>
            <p className="text-xs text-neutral-500">
              A link that lets clients and printers mark corrections without an account.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <IconClose className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {planBlocked ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="mb-1 font-semibold">External review links are a Business feature</p>
              <p className="text-amber-800">
                Upgrade to Business to share proofs with clients who don't have an account.
              </p>
            </div>
          ) : (
            <>
              {/* ── The warning ─────────────────────────────────────────── */}
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3.5 text-sm">
                <p className="mb-1.5 font-semibold text-amber-900">
                  Anyone with this link can open the PDF
                </p>
                <ul className="mb-3 list-disc space-y-1 pl-5 text-[13px] leading-snug text-amber-900/90">
                  <li>No sign-in, no password — the link itself is the key.</li>
                  <li>
                    Anyone it's forwarded to can read this PDF, see every comment on it, and add
                    corrections <strong>anonymously</strong>.
                  </li>
                  <li>Comments from the link show a self-declared name that nobody verifies.</li>
                  <li>You can turn the link off at any time, but you can't un-share what was seen.</li>
                </ul>
              </div>

              {/* ── Options ─────────────────────────────────────────────── */}
              <div className="mt-4 space-y-2.5">
                <Toggle
                  checked={canComment}
                  onChange={setCanComment}
                  label="Let them add corrections"
                  hint="Off makes the link view-only."
                />
                <Toggle
                  checked={allowDownload}
                  onChange={setAllowDownload}
                  label="Let them download the original PDF"
                  hint="Off means they can only view it in the browser."
                />
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <span className="w-28 shrink-0">Stops working</span>
                  <select
                    value={expiresInDays ?? ''}
                    onChange={(e) => setExpiresInDays(e.target.value ? Number(e.target.value) : null)}
                    className="rounded border border-neutral-300 bg-white px-2 py-1 text-sm outline-none focus:border-neutral-900"
                  >
                    {EXPIRY_DAY_CHOICES.map((d) => (
                      <option key={d} value={d}>
                        in {d} days
                      </option>
                    ))}
                    <option value="">never</option>
                  </select>
                </label>
              </div>

              {/* ── Consent ─────────────────────────────────────────────── */}
              {/* Sits against the button, not up in the warning box: by the time
                  someone has scrolled past the options they've forgotten the box,
                  and the tick has to be the thing they do right before creating. */}
              <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[13px] font-medium text-amber-900">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
                />
                I understand this PDF will be readable by anyone holding the link.
              </label>

              <Button
                className="mt-2 w-full"
                disabled={!acknowledged || create.isPending}
                onClick={() => create.mutate()}
              >
                {create.isPending ? 'Creating…' : 'Create link & copy'}
              </Button>

              {/* ── Existing links ──────────────────────────────────────── */}
              <div className="mt-5">
                {links.isPending ? (
                  <div className="flex justify-center py-4">
                    <Spinner />
                  </div>
                ) : live.length === 0 && past.length === 0 ? (
                  <p className="py-2 text-center text-xs text-neutral-400">
                    No links have been created for this PDF.
                  </p>
                ) : (
                  <>
                    {live.length > 0 && (
                      <>
                        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                          Active links
                        </h3>
                        <ul className="space-y-2">
                          {live.map((l) => (
                            <LinkRow key={l.id} link={l} onChanged={invalidate} />
                          ))}
                        </ul>
                      </>
                    )}
                    {past.length > 0 && (
                      <>
                        <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                          Turned off
                        </h3>
                        <ul className="space-y-2 opacity-60">
                          {past.map((l) => (
                            <LinkRow key={l.id} link={l} onChanged={invalidate} />
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

function LinkRow({ link, onChanged }: { link: ShareLink; onChanged: () => void }) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const dead = !!link.revokedAt || isExpired(link);

  const revoke = useMutation({
    mutationFn: () => revokeShareLink(link.id),
    onSuccess: onChanged,
    onError: (e: Error) => toast(e.message),
  });
  const setComment = useMutation({
    mutationFn: (v: boolean) => updateShareLink(link.id, { canComment: v }),
    onSuccess: onChanged,
    onError: (e: Error) => toast(e.message),
  });

  return (
    <li className="rounded-lg border border-neutral-200 p-2.5">
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-neutral-50 px-2 py-1 text-[11px] text-neutral-600">
          {link.url || 'Link unavailable — created before encryption was configured'}
        </code>
        {!dead && link.url && (
          <button
            type="button"
            onClick={() => void copy(link.url, toast)}
            className="shrink-0 rounded border border-neutral-200 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
          >
            Copy
          </button>
        )}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
        <span>{link.viewCount} views</span>
        <span>{link.commentCount} comments</span>
        {link.guestCount > 0 && <span>{link.guestCount} people</span>}
        {link.revokedAt ? (
          <span className="text-red-500">turned off</span>
        ) : isExpired(link) ? (
          <span className="text-red-500">expired</span>
        ) : link.expiresAt ? (
          <span>expires {new Date(link.expiresAt).toLocaleDateString()}</span>
        ) : (
          <span>no expiry</span>
        )}
        {!link.canComment && <span>view-only</span>}
        {link.allowDownload && <span>downloads on</span>}

        {!dead && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setComment.mutate(!link.canComment)}
              disabled={setComment.isPending}
              className="rounded px-1 py-0.5 font-medium text-neutral-600 hover:bg-neutral-100"
            >
              {link.canComment ? 'Make view-only' : 'Allow comments'}
            </button>
            {confirming ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(false);
                    revoke.mutate();
                  }}
                  className="rounded px-1 py-0.5 font-semibold text-red-600 hover:bg-red-50"
                >
                  Turn off
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded px-1 py-0.5 text-neutral-400 hover:text-neutral-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="rounded px-1 py-0.5 font-medium text-red-600 hover:bg-red-50"
              >
                Turn off
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm text-neutral-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-neutral-900"
      />
      <span>
        {label}
        <span className="block text-xs text-neutral-400">{hint}</span>
      </span>
    </label>
  );
}

function isExpired(link: ShareLink): boolean {
  return link.expiresAt != null && new Date(link.expiresAt).getTime() <= Date.now();
}

/** Copy to the clipboard, falling back to a prompt-free toast on failure. */
async function copy(url: string, toast: (m: string) => void): Promise<void> {
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied to your clipboard.');
  } catch {
    toast('Copy failed — select the link and copy it manually.');
  }
}
